/**
 * Runtime config loader for the admin SPA.
 *
 * The SPA is built ONCE for all envs (prod, future staging) and reads its
 * Cognito + admin-api endpoint values from `/config.json` at boot. Why
 * runtime instead of Vite build-time:
 *  - VITE_COGNITO_CLIENT_ID only exists after `cdk deploy` mints the
 *    Cognito UserPoolClient. Build-time env baking would require a
 *    two-pass deploy (deploy CDK → read CFN output → rebuild SPA →
 *    redeploy). Runtime fetch dodges that.
 *  - CDK writes `/config.json` to the SPA bucket via
 *    `s3deploy.Source.jsonData` (see infra/lib/admin-stack.ts), so every
 *    deploy gets the current values for free.
 *
 * Local dev shortcut: when VITE_LOCAL_DEV=1 (set by `pnpm admin-spa:local`
 * via .env.local), the loader does NOT fetch /config.json — it returns a
 * stub pointing at the local admin-api on :4001 with empty Cognito values
 * so the SPA short-circuits sign-in via the AuthProvider's local-dev
 * branch.
 */

export interface AdminConfig {
  adminApiBaseUrl: string;
  cognitoDomain: string;
  cognitoClientId: string;
  cognitoRedirectUri: string;
  cognitoLogoutUri: string;
}

let cached: AdminConfig | null = null;

const LOCAL_DEV_ENV = (import.meta.env?.VITE_LOCAL_DEV as string | undefined) === '1';

/**
 * Belt-and-suspenders: VITE_LOCAL_DEV alone isn't enough to skip the
 * /config.json fetch — we also require the page to be served from
 * localhost. Otherwise a stale .env.local on a build host (Vite loads
 * .env.local in ALL modes, including `vite build`) would silently
 * make the deployed admin.outlinked.ai SPA fall into the local-dev stub.
 */
function isLocalDevHost(): boolean {
  if (typeof window === 'undefined' || !window.location) return false;
  const h = window.location.hostname;
  return h === 'localhost' || h === '127.0.0.1' || h === '0.0.0.0';
}

function shouldUseLocalStub(): boolean {
  return LOCAL_DEV_ENV && isLocalDevHost();
}

const LOCAL_STUB: AdminConfig = {
  adminApiBaseUrl:
    (import.meta.env?.VITE_ADMIN_API_BASE_URL as string | undefined) ?? 'http://localhost:4001',
  cognitoDomain: '',
  cognitoClientId: '',
  cognitoRedirectUri: '',
  cognitoLogoutUri: '',
};

export async function loadConfig(): Promise<AdminConfig> {
  if (cached) return cached;
  if (shouldUseLocalStub()) {
    cached = LOCAL_STUB;
    return cached;
  }
  const res = await fetch('/config.json', { cache: 'no-cache' });
  if (!res.ok) {
    throw new Error(`Failed to load /config.json: HTTP ${res.status}`);
  }
  // CloudFront's 403/404 → /index.html rewrite (for SPA client-side routing,
  // see admin-stack.ts errorResponses) means a missing /config.json
  // returns HTML at HTTP 200. Without this content-type guard, the next
  // `.json()` call would throw a confusing JSON parse error rather than
  // the diagnosable "config returned HTML" the deployer needs to see.
  const contentType = res.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    throw new Error(
      `Failed to load /config.json: server returned ${contentType || 'no'} content-type, ` +
        'expected application/json (CloudFront likely fell through to /index.html)',
    );
  }
  cached = (await res.json()) as AdminConfig;
  return cached;
}

export function getConfig(): AdminConfig {
  if (!cached) {
    throw new Error('Config not loaded yet — call loadConfig() before accessing.');
  }
  return cached;
}

// Test-only helper. Allows tests to inject a synthetic config without
// hitting fetch. Production code paths must not call this.
export function _setConfigForTest(config: AdminConfig | null): void {
  cached = config;
}
